import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useRequest } from 'domain/core/hooks/useRequest';
import { ApiMethodsEnum } from 'infrastructure/enums/ApiMethodsEnum';
import { storageService } from 'infrastructure/services/StorageService';
import { TUpdate } from 'domain/types';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { useContextSelector as useCS } from 'use-context-selector';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { useLocation } from 'react-router-dom';
import { useStateMachine } from 'little-state-machine';
import { updateBeforeEditAction } from 'view/pages/createJobPost/helpers/updateBeforeEditAction';
import { showSystemToast } from 'view/components/SystemToast';

export const useUpdateJobPostByStep = (linkTo: string): any => {
  const navigate = useNavigate();
  const location = useLocation();
  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );
  const [jobPostId, setJobPostId] = useState<number | null>(null);
  const [isDraftPost, setIsDraftPost] = useState<boolean | null>(null);
  const [isPreviewPost, setIsPreviewPost] = useState<boolean | null>(false);

  const { actions } = useStateMachine({
    actions: { updateBeforeEditAction },
  });

  useEffect(() => {
    if (location.pathname === '/candidates/my-job-posts') return;
    storageService.getItem('createJobPostId').then((jobPost) => {
      if (!jobPost) navigate('/profile/profile-settings', { replace: true });
      setJobPostId(Number(jobPost));
    });
  }, []);

  const { data, isLoading, status, error, send } = useRequest({
    url: `/api/v1/jobPosts/${jobPostId}`,
    method: ApiMethodsEnum.Patch,
    sendOnStart: false,
  });

  const updateJobPostByStep = (formData: any, isDraft = null, isPreview?: boolean) => {
    setIsDraftPost(isDraft);
    if (isPreview) setIsPreviewPost(isPreview);
    send(formData).then();
  };

  const updateJobPostForEditing = (formData: any, jobPostId: any) => {
    send(formData, `/api/v1/jobPosts/${jobPostId}`).then();
  };

  useEffect(() => {
    if (isLoading || !data || error || status !== 200) return;
    if (update) {
      update({ createJobPostData: data, screeningQuestions: data?.faq || [] });
      if (data?.editStatus === 'edit') {
        const dataToSave = JSON.parse(JSON.stringify(data));
        delete dataToSave?.clicksCounter;
        delete dataToSave?.createdAt;
        delete dataToSave?.updatedAt;
        delete dataToSave?.viewsCounter;
        for (const key in data) {
          if (dataToSave[key] === null || dataToSave[key] === '') {
            delete dataToSave[key];
          }
        }
        actions.updateBeforeEditAction(dataToSave);
      }
      if (isPreviewPost) {
        navigate('/profile/job-post-preview', { replace: true });
        return;
      }
      if (isDraftPost) {
        if (data?.company?.status === 'PENDING_REVIEW') {
          showSystemToast(
            'info',
            'Job post saved as draft. It cannot be published until your company and membership are approved.'
          );
        }
        update({ createJobPostData: null, screeningQuestions: [], isOpenBackToHomeModal: false });
        storageService.removeItem('createJobPostId').then();
        navigate('/candidates/my-job-posts?postStatus=draft', { replace: true });
        return;
      }
      if (typeof isDraftPost === 'boolean' && !isDraftPost) {
        update({ isOpenAfterSubmitJobPostModal: true });
        return;
      }
    }
    setJobPostId(null);
    navigate(linkTo, { replace: true }); // /create-job-post/step1
  }, [data, isLoading, error, status, isDraftPost]);

  return {
    isLoading,
    error,
    updateJobPostByStep,
    updateJobPostForEditing,
  };
};
